import { useCallback, useMemo } from 'react';

import {
  displaySequencedFlow,
  finishSelected,
  goToNextSelected,
  goToPreviousSelected,
} from '../../actions/contentItemActions';
import { useIsMobileV2 } from '../../hooks/useIsMobile';
import { HistoryItem } from '../../store/reducers/currentSequencedFlowPositionReducer';
import { getItemByIdSelector } from '../../store/reducers/items/itemsSelectors';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { useContentRequirementsContext } from '../RestrictNavigation/ContentRequirementsProvider';
import { useContentDisplayed } from './components/mobile/hooks/useContentDisplayed';
import { useSequencedContentFlowContext } from './context/SequencedContentFlowContext';
import { calculateNextItemsId } from './utils';
import { isAutoProgressableItem, isSection, isSequencedFlowStep, ModalSize, isContentEmpty } from '@ael/core-api';
import { Stack } from 'immutable';
import { useDispatch } from 'react-redux';

interface NavigationProps {
  history: Stack<HistoryItem>;
}

const useProgressEnabled = () => {
  const requirements = useContentRequirementsContext();

  return requirements.fulfilled;
};

export const useSequencedFlowNavigation = (ownProps: NavigationProps) => {
  const dispatch = useDispatch();
  const { currentContentItem, isLastContentItem, sequencedFlowItem, currentSection } = useSequencedContentFlowContext();
  const progressEnabled = useProgressEnabled();
  const getItemById = useStudentSelector(getItemByIdSelector);
  const previousContentItems = ownProps.history;
  const { contentDisplayed } = useContentDisplayed(currentContentItem);
  const isMobile = useIsMobileV2();

  const previousExists = previousContentItems.size > 0 && !!previousContentItems.peek();

  const isCurrentContentItemSection = isSection(currentContentItem);

  // nextItem should be undefined if current item is section.
  const nextItem =
    isLastContentItem || isCurrentContentItemSection
      ? undefined
      : getItemById(calculateNextItemsId(currentContentItem.id, currentSection));

  const goToNext = () => {
    if (nextItem) {
      dispatch(goToNextSelected(nextItem));
    }
  };
  const goToPrevious = () => {
    dispatch(goToPreviousSelected);
  };
  const finishItem = () => () => {
    dispatch(finishSelected(sequencedFlowItem.id, isMobile));
  };
  const displayActionContent = useCallback(() => dispatch(displaySequencedFlow()), [dispatch]);

  const displaySequencedFlowContent = useMemo(
    () =>
      isSequencedFlowStep(currentContentItem) &&
      (isMobile || currentContentItem.modalSize === ModalSize.Fullscreen) &&
      !contentDisplayed &&
      !isContentEmpty(currentContentItem.content) &&
      currentContentItem.position
        ? () => displayActionContent()
        : undefined,
    [contentDisplayed, currentContentItem, displayActionContent, isMobile]
  );

  return {
    currentItemId: sequencedFlowItem?.id,
    goToNext: nextItem && !isCurrentContentItemSection ? () => goToNext() : undefined,
    onFinish: !nextItem && !isCurrentContentItemSection && sequencedFlowItem ? finishItem() : undefined,
    goToPrevious: previousExists ? goToPrevious : undefined,
    progressDisabled: !progressEnabled,
    autoProgress: isAutoProgressableItem(currentContentItem) ? currentContentItem.restriction.autoProgress : undefined,
    // The content should be 'hidden' if:
    // MOBILE: Is SQ step AND state.currentSequencedFlowPosition.displayed is false AND content is not empty AND has a position.
    // DESKTOP: Is SQ step AND modalSize is Fullscreen AND state.currentSequencedFlowPosition.displayed is false AND content is not empty AND step has a position.
    displaySequencedFlowContent: displaySequencedFlowContent,
  };
};
